import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import AdminHeader from '../components/AdminHeader';
import AdminMainOverview from './AdminMainOverview';
import { useAuth } from '../AuthContext';

const AdminDashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [todos, setTodos] = useState([]);
  const [newTodoText, setNewTodoText] = useState('');
  const [loading, setLoading] = useState(true);

  // Check authentication
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  // Fetch To-Dos
  const fetchTodos = async () => {
    try {
      const todosSnapshot = await getDocs(collection(db, 'todos'));
      const todosList = todosSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      todosList.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setTodos(todosList);
    } catch (error) {
      console.error('Error fetching todos:', error);
    }
  };

  // Add To-Do
  const handleAddTodo = async () => {
    if (!newTodoText.trim()) return;

    try {
      await addDoc(collection(db, 'todos'), {
        text: newTodoText,
        completed: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      setNewTodoText('');
      fetchTodos();
    } catch (error) {
      console.error('Error adding todo:', error);
    }
  };

  // Toggle To-Do completion
  const handleToggleTodo = async (todoId, completed) => {
    try {
      await updateDoc(doc(db, 'todos', todoId), {
        completed: !completed,
        updatedAt: new Date().toISOString()
      });
      fetchTodos();
    } catch (error) {
      console.error('Error toggling todo:', error);
    }
  };

  // Delete To-Do
  const handleDeleteTodo = async (todoId) => {
    try {
      await deleteDoc(doc(db, 'todos', todoId));
      fetchTodos();
    } catch (error) {
      console.error('Error deleting todo:', error);
    }
  };

  // Fetch todos when component mounts
  useEffect(() => {
    if (isAuthenticated) {
      fetchTodos();
      setLoading(false);
    }
  }, [isAuthenticated]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0d] text-white admin-page">
        <AdminHeader />
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="mt-4">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-[#0d0d0d] text-white admin-page">
      <AdminHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-gray-400 mt-1">Welcome back, {user?.displayName || user?.email}</p>
          </div>
          <button
            onClick={handleLogout}
            className="bg-red-600 hover:bg-red-700 px-4 py-2 rounded text-sm font-medium transition-colors"
          >
            Logout
          </button>
        </div>

        {/* Main Overview Component */}
        <AdminMainOverview />
      </div>
    </div>
  );
};

export default AdminDashboard; 